import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Grid,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import GroupsIcon from "@mui/icons-material/Groups";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import SearchIcon from "@mui/icons-material/Search";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import GavelIcon from "@mui/icons-material/Gavel";
import WorkIcon from "@mui/icons-material/Work";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterListIcon from "@mui/icons-material/FilterList";
import AdminApi from "Service/Admin.api";
import ColorData from "Components/CstmTheme/ColorData";
import { useDispatch } from "react-redux";
import { startLoading, stopLoading, setAlertData } from "../../Redux/Screen/Action";
import { StyledTableCell, StyledTableRow } from "./ProfessionalUserList";
import { RouteUrls } from "Constants/RouteUrls";

// ─── Types ────────────────────────────────────────────────────────────────────

interface AssignedProfessional {
  professionalId: number;
  fullName: string;
  email: string;
  role: string;
  consentStatus: string;
}

interface AssignedCase {
  pkCase: number;
  caseNumber: string;
  orderType?: {
    pkOrderType: number;
    orderTypeName: string;
    orderPrice: number;
  };
  currentProcessingStatus?: {
    pkProcessingStatus: number;
    statusName: string;
  };
  assignedProfessionals?: AssignedProfessional[];
  assignedProfessionalId?: string | null;
  client?: {
    firstName: string;
    lastName: string;
    email: string;
    contactNumber: string;
  };
  casePrice?: number;
  dateCreated?: string;
}

// ─── Status colour helper ─────────────────────────────────────────────────────
const statusColor = (status: string): "default" | "info" | "success" | "warning" | "error" => {
  const s = status?.toLowerCase() ?? "";
  if (s.includes("complete") || s.includes("closed")) return "success";
  if (s.includes("progress") || s.includes("active") || s.includes("hearing")) return "info";
  if (s.includes("pending") || s.includes("review")) return "warning";
  if (s.includes("cancel") || s.includes("reject")) return "error";
  return "default";
};

// ─── Consent colour ───────────────────────────────────────────────────────────
const consentColor = (status: string): "default" | "success" | "error" | "warning" => {
  const s = status?.toLowerCase() ?? "";
  if (s === "confirmed" || s === "accepted") return "success";
  if (s === "declined") return "error";
  return "warning";
};

// ─── Avatar colour from name ──────────────────────────────────────────────────
const avatarColors = [
  "#5c6bc0", "#26a69a", "#ef5350", "#42a5f5", "#66bb6a",
  "#ffa726", "#ab47bc", "#ec407a", "#8d6e63", "#78909c",
];
const getAvatarColor = (name: string) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return avatarColors[Math.abs(hash) % avatarColors.length];
};

// ─── ODR type check ───────────────────────────────────────────────────────────
const ODR_TYPES = ["Online Mediation", "Online Arbitration", "Arbitration", "Online Negotiation"];
const isOdr = (orderTypeName?: string) => ODR_TYPES.includes(orderTypeName ?? "");

// ─────────────────────────────────────────────────────────────────────────────
const AssignedCasesPanelList: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();

  const [cases, setCases] = useState<AssignedCase[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [orderTypeFilter, setOrderTypeFilter] = useState("All");

  // ── Fetch ─────────────────────────────────────────────────────────────────

  const fetchAssignedCases = useCallback(async () => {
    setLoading(true);
    dispatch(startLoading({ loading: true, loadtext: "Loading assigned cases..." }));
    try {
      const res = await AdminApi.fetchCases();
      if (res?.success && Array.isArray(res.data)) {
        // Filter: only cases that have at least one professional assigned via the panel
        const assigned = res.data.filter(
          (c: any) =>
            (c.assignedProfessionals && c.assignedProfessionals.length > 0) ||
            c.assignedProfessionalId != null
        );
        setCases(assigned.sort((a: any, b: any) => b.pkCase - a.pkCase));
      } else {
        setCases([]);
      }
    } catch (err) {
      console.error(err);
      dispatch(
        setAlertData({
          alertMessage: "Failed to load assigned cases.",
          alertVariant: "error",
          openAlert: true,
        })
      );
    } finally {
      setLoading(false);
      dispatch(stopLoading());
    }
  }, [dispatch]);

  useEffect(() => {
    fetchAssignedCases();
  }, [fetchAssignedCases]);

  // ── Filtering ─────────────────────────────────────────────────────────────

  const orderTypes = useMemo(() => {
    const types = Array.from(
      new Set(cases.map((c) => c.orderType?.orderTypeName).filter(Boolean))
    ) as string[];
    return ["All", ...types];
  }, [cases]);

  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    return cases.filter((c) => {
      const typeName = c.orderType?.orderTypeName ?? "";
      const matchType = orderTypeFilter === "All" || typeName === orderTypeFilter;
      if (!matchType) return false;
      if (!q) return true;
      const caseNum = (c.caseNumber ?? "").toLowerCase();
      const clientName = `${c.client?.firstName ?? ""} ${c.client?.lastName ?? ""}`.toLowerCase();
      const clientEmail = (c.client?.email ?? "").toLowerCase();
      const status = (c.currentProcessingStatus?.statusName ?? "").toLowerCase();
      const orderType = typeName.toLowerCase();
      const profNames = (c.assignedProfessionals ?? [])
        .map((p) => p.fullName)
        .join(" ")
        .toLowerCase();
      return (
        caseNum.includes(q) ||
        clientName.includes(q) ||
        clientEmail.includes(q) ||
        status.includes(q) ||
        orderType.includes(q) ||
        profNames.includes(q)
      );
    });
  }, [cases, searchQuery, orderTypeFilter]);

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading && cases.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={56} />
      </Box>
    );
  }

  return (
    <Box sx={{ py: 3, px: { xs: 2, md: 3 }, minHeight: "100vh", backgroundColor: "#f7f8fc" }}>
      {/* Back */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 2, fontWeight: 600, textTransform: "none" }}
      >
        Back
      </Button>

      {/* Header */}
      <Box display="flex" alignItems="center" gap={1.5} mb={0.5}>
        <ManageAccountsIcon sx={{ fontSize: 34, color: ColorData.primary.main }} />
        <Typography variant="h5" fontWeight={700}>
          Manage Assigned Case Panels
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" mb={3}>
        All cases with at least one professional already assigned. Click&nbsp;
        <strong>Manage Panel</strong> to add, remove, or reassign professionals for any case.
      </Typography>

      {/* Stats row */}
      <Grid container spacing={2} mb={3}>
        {[
          {
            label: "Total Assigned Cases",
            value: cases.length,
            icon: <GroupsIcon />,
            color: ColorData.primary.main,
          },
          {
            label: "ODR Cases",
            value: cases.filter((c) => isOdr(c.orderType?.orderTypeName)).length,
            icon: <GavelIcon />,
            color: "#5c6bc0",
          },
          {
            label: "Non-ODR Cases",
            value: cases.filter((c) => !isOdr(c.orderType?.orderTypeName)).length,
            icon: <WorkIcon />,
            color: "#26a69a",
          },
          {
            label: "Filtered View",
            value: filtered.length,
            icon: <FilterListIcon />,
            color: "#ef5350",
          },
        ].map(({ label, value, icon, color }) => (
          <Grid size={{ xs: 6, sm: 3 }} key={label}>
            <Paper
              sx={{
                p: 2,
                borderRadius: 3,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                boxShadow: "0 2px 10px rgba(0,0,0,0.07)",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: `${color}18`,
                  color: color,
                  flexShrink: 0,
                }}
              >
                {icon}
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                  {value}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {label}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, borderRadius: 2, mb: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 6, md: 7 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by case no., client, status, professional name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="primary" />
                  </InputAdornment>
                ),
              }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
          </Grid>
          <Grid size={{ xs: 10, sm: 4, md: 4 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Order Type</InputLabel>
              <Select
                value={orderTypeFilter}
                label="Order Type"
                onChange={(e) => setOrderTypeFilter(e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                {orderTypes.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 2, sm: 2, md: 1 }}>
            <Tooltip title="Refresh list">
              <Button
                variant="outlined"
                size="medium"
                onClick={fetchAssignedCases}
                disabled={loading}
                sx={{ minWidth: 0, px: 1.5, borderRadius: 2 }}
              >
                <RefreshIcon fontSize="small" />
              </Button>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper sx={{ borderRadius: 3, overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}>
        <TableContainer sx={{ maxHeight: "calc(100vh - 380px)", overflowY: "auto" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <StyledTableCell sx={{ minWidth: 120 }}>Case No.</StyledTableCell>
                <StyledTableCell sx={{ minWidth: 130 }}>Order Type</StyledTableCell>
                <StyledTableCell sx={{ minWidth: 140 }}>Client</StyledTableCell>
                <StyledTableCell sx={{ minWidth: 130 }}>Status</StyledTableCell>
                <StyledTableCell sx={{ minWidth: 200 }}>Panel Members</StyledTableCell>
                <StyledTableCell align="center" sx={{ minWidth: 130 }}>
                  Action
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <Box textAlign="center">
                      <GroupsIcon sx={{ fontSize: 52, color: "#ccc", mb: 1 }} />
                      <Typography color="text.secondary">
                        {searchQuery || orderTypeFilter !== "All"
                          ? "No cases match your search."
                          : "No cases with assigned professionals found."}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((c) => {
                  const panelMembers = c.assignedProfessionals ?? [];
                  const memberCount = panelMembers.length;

                  const orderTypeName = c.orderType?.orderTypeName ?? "";
                  const statusName = c.currentProcessingStatus?.statusName ?? "";

                  return (
                    <StyledTableRow key={c.pkCase}>
                      {/* Case Number */}
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          {isOdr(orderTypeName) && (
                            <GavelIcon sx={{ fontSize: 14, color: "#5c6bc0" }} />
                          )}
                          <Typography variant="body2" fontWeight={700} color="primary">
                            {c.caseNumber || `#${c.pkCase}`}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Order Type */}
                      <TableCell>
                        <Chip
                          label={orderTypeName || "—"}
                          size="small"
                          color={isOdr(orderTypeName) ? "info" : "default"}
                          variant="outlined"
                          sx={{ fontWeight: 600, fontSize: "0.72rem" }}
                        />
                      </TableCell>

                      {/* Client */}
                      <TableCell>
                        {c.client ? (
                          <>
                            <Typography variant="body2" fontWeight={600} noWrap>
                              {c.client.firstName} {c.client.lastName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap display="block">
                              {c.client.contactNumber || c.client.email}
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        <Chip
                          label={statusName || "—"}
                          size="small"
                          color={statusColor(statusName)}
                          sx={{ fontWeight: 600, fontSize: "0.72rem" }}
                        />
                      </TableCell>

                      {/* Panel Members */}
                      <TableCell>
                        {memberCount === 0 ? (
                          <Chip
                            label="No panel members"
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                        ) : (
                          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                            <AvatarGroup
                              max={3}
                              sx={{
                                "& .MuiAvatar-root": {
                                  width: 28,
                                  height: 28,
                                  fontSize: "0.72rem",
                                  border: "2px solid white",
                                },
                              }}
                            >
                              {panelMembers.map((p) => (
                                <Tooltip
                                  key={p.professionalId}
                                  title={`${p.fullName} — ${p.role} (${p.consentStatus})`}
                                  arrow
                                >
                                  <Avatar
                                    sx={{
                                      bgcolor: getAvatarColor(p.fullName ?? "P"),
                                      width: 28,
                                      height: 28,
                                      fontSize: "0.72rem",
                                    }}
                                  >
                                    {(p.fullName ?? "P").charAt(0).toUpperCase()}
                                  </Avatar>
                                </Tooltip>
                              ))}
                            </AvatarGroup>
                            <Box>
                              <Typography variant="caption" fontWeight={700} display="block">
                                {memberCount} member{memberCount !== 1 ? "s" : ""}
                              </Typography>
                              {/* Show first member's role + consent */}
                              {panelMembers[0] && (
                                <Box display="flex" gap={0.5} flexWrap="wrap">
                                  <Chip
                                    label={panelMembers[0].role || "Professional"}
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    sx={{ fontSize: "0.65rem", height: 18 }}
                                  />
                                  <Chip
                                    label={panelMembers[0].consentStatus || "Assigned"}
                                    size="small"
                                    color={consentColor(panelMembers[0].consentStatus)}
                                    sx={{ fontSize: "0.65rem", height: 18 }}
                                  />
                                </Box>
                              )}
                            </Box>
                          </Box>
                        )}
                      </TableCell>

                      {/* Action */}
                      <TableCell align="center">
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<PersonAddIcon />}
                          onClick={() =>
                            navigate(`/${RouteUrls.ManageCasePanel}/${c.pkCase}`)
                          }
                          sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: "0.78rem",
                            background: ColorData.primary.main,
                            color: "white",
                            borderRadius: 2,
                            "&:hover": {
                              background: ColorData.hover.main,
                              color: ColorData.text.primary,
                            },
                          }}
                        >
                          Manage Panel
                        </Button>
                      </TableCell>
                    </StyledTableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Footer count */}
        <Box
          sx={{
            px: 3,
            py: 1.5,
            borderTop: "1px solid",
            borderColor: "divider",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Showing <strong>{filtered.length}</strong> of <strong>{cases.length}</strong> assigned
            cases
          </Typography>
          {loading && <CircularProgress size={16} />}
        </Box>
      </Paper>
    </Box>
  );
};

export default AssignedCasesPanelList;
